import jsPDF from 'jspdf';

import { FormSummaryConstants, RiskControlFormConstants } from './pdfConstants';
import {
  PDFPage,
  PDFResult,
  PDFText,
  ImageData,
  VerticalAlignType,
  PDFTableItem,
  FontStyle,
} from '../models/pdfModal';
import {
  FormModel,
  Question,
  QuestionPDF,
  QuestionPosition,
  QuestionWithMarks,
} from '../models/questionModel';

const generatePDFText = async (
  doc: jsPDF,
  {
    value,
    x,
    y,
    fontsize = 10,
    fontColor = '#313131',
    fontStyle = 'normal',
    maxWidth = 0,
    charSpace = 0,
    lineHeight = 1.15,
    isCheckbox = false,
    imageObject = null,
    textAlign,
    fullWidth = false,
    box = null,
    circle = null,
    textRotate = 0,
  }: PDFText
) => {
  if (isCheckbox) {
    const tick = await imageToBase64('/images/Tick.png');
    if (tick) {
      doc.addImage({
        imageData: tick,
        x: x,
        y: y,
        width: fontsize,
        height: fontsize,
      });
    }
  } else if (imageObject) {
    const aspectRatio = imageObject.imageWidth / imageObject.imageHeight;
    const height = imageObject.maxHeight;
    const width = aspectRatio * imageObject.maxHeight;

    if (value) {
      doc.addImage({
        imageData: value,
        x: x + 1 - width / 2,
        y: y - height + 1,
        width: width,
        height: height,
      });
    }
  } else if (box) {
    let type: string = 'FD';
    if (box.color && !box.strokeColor) {
      type = 'F';
    } else if (!box.color && box.strokeColor) {
      type = 'S';
    }

    doc.setFillColor(box.color || '');
    doc.setDrawColor(box.strokeColor || '');
    doc.setLineWidth(box.strokeWidth || 0);
    doc.rect(x, y, box.width, box.height, type);
  } else if (circle) {
    let style: string = 'FD';
    if (circle.fill && !circle.stroke) {
      style = 'F';
    } else if (!circle.fill && circle.stroke) {
      style = 'S';
    }

    doc.setFillColor(circle.fill || '');
    doc.setDrawColor(circle.stroke || '');
    doc.setLineWidth(circle.strokeWidth || 0);

    doc.circle(x, y, circle.radius, style);
  } else {
    /** Default fonts
     *
     * helvetica: [ 'normal', 'bold', 'italic', 'bolditalic' ],
     * Helvetica: [ '', 'Bold', 'Oblique', 'BoldOblique' ],
     * courier: [ 'normal', 'bold', 'italic', 'bolditalic' ],
     * Courier: [ '', 'Bold', 'Oblique', 'BoldOblique' ],
     * times: [ 'normal', 'bold', 'italic', 'bolditalic' ],
     * Times: [ 'Roman', 'Bold', 'Italic', 'BoldItalic' ],
     * zapfdingbats: [ 'normal' ],
     * ZapfDingbats: [ '' ],
     * symbol: [ 'normal' ],
     * Symbol: [ '' ]
     */

    doc.setFontSize(fontsize);
    doc.setTextColor(fontColor);
    doc.setFont('helvetica', fontStyle);

    const xPos =
      fullWidth && textAlign == 'center' ? doc.internal.pageSize.width / 2 : x;

    doc.text(value.toString() || '', xPos, y, {
      maxWidth: fullWidth ? doc.internal.pageSize.width : maxWidth,
      charSpace: charSpace,
      lineHeightFactor: lineHeight,
      align: textAlign,
      angle: textRotate,
    });
  }
};

type GenericFormType = 'summary' | 'riskControl';

export const imageToBase64 = async (filePath: string): Promise<string> => {
  // Fetch the image from the public folder
  const response = await fetch(filePath);

  // Get the image as a Blob
  const blob = await response.blob();

  // Convert Blob to Base64 using FileReader
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      resolve(reader.result as string);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

export const getImageData = (file: File): Promise<ImageData> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      const img = new Image();
      const base64 = event.target?.result as string;

      img.onload = () => {
        resolve({
          width: img.width,
          height: img.height,
          base64: base64,
        });
      };

      img.onerror = () => {
        reject(new Error('Failed to load image.'));
      };

      img.src = base64;
    };

    reader.onerror = () => {
      reject(new Error('Failed to read the file.'));
    };

    reader.readAsDataURL(file);
  });
};

export const generatePDF = async (
  filename: string,
  pages: PDFPage[]
): Promise<PDFResult> => {
  const doc = getDocSetup();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  doc.setFontSize(8);

  let index = 0;
  for (const det of pages) {
    if (index > 0) {
      doc.addPage();
    }

    if (det.background) {
      const background = await imageToBase64(det.background);

      if (background) {
        doc.addImage({
          imageData: background,
          x: 0,
          y: 0,
          width: pageWidth,
          height: pageHeight,
        });
      }
    }

    for (const txt of det.texts) {
      if (!txt.hide) {
        await generatePDFText(doc, txt);
      }
    }

    index++;
  }

  // doc.save(`${filename}.pdf`); // Saving the file

  const pdfBlob = doc.output('blob');
  const pdfFile = new File([pdfBlob], `${filename}.pdf`, {
    type: 'application/pdf',
  });
  return {
    file: pdfFile,
    blob: pdfBlob,
    filename: filename,
  };
};

export const getDocSetup = (): jsPDF => {
  return new jsPDF({
    orientation: 'p',
    unit: 'mm',
    format: 'a4',
  });
};

export const generatePDFTable = (
  items: PDFTableItem[][],
  rowHeight: number[],
  colWidth: number[],
  x: number,
  y: number
): PDFText[] => {
  return items
    .map((r, rInd) => {
      const currentHeight = rowHeight[rInd];
      const previousHeight = rowHeight
        .slice(0, rInd)
        .reduce((a, b) => a + b, 0);

      return r
        .map((c, cInd) => {
          const currentWidth = colWidth[cInd];
          const previousWidth = colWidth
            .slice(0, cInd)
            .reduce((a, b) => a + b, 0);

          return generatePDFTextInBox(
            {
              id: `box_1_${cInd + 1}`,
              value: c.value,
              x: x + previousWidth,
              y: y + previousHeight,
              box: {
                height: currentHeight,
                width: currentWidth,
                color: c.fill,
                strokeColor: '#000000',
                strokeWidth: 0.1,
              },
              textAlign: c.textAlign || 'left',
              fontStyle: c.fontStyle,
              fontsize: c.fontSize,
            },
            1,
            c.vAlign || 'top'
          );
        })
        .flat();
    })
    .flat();
};

export const generatePDFTextInBox = (
  details: PDFText,
  padding: number = 0,
  vAlign: VerticalAlignType = 'top'
): PDFText[] => {
  const doc = getDocSetup();
  if (!details.box) return [];

  doc.setFontSize(details.fontsize || 10);
  const textDimension = doc.getTextDimensions(details.value);

  let valueX = details.x + padding;
  let valueY =
    details.y + textDimension.h / (details.lineHeight || 1.15) + padding;

  if (details.textAlign == 'center') {
    valueX = details.x + details.box.width / 2;
  } else if (details.textAlign == 'right') {
    valueX = details.x + details.box.width - padding;
  }

  const lines = doc.splitTextToSize(details.value, details.box.width - padding);
  const textHeight = textDimension.h * lines.length;

  if (vAlign == 'middle') {
    valueY =
      details.y +
      textDimension.h / (details.lineHeight || 1.15) +
      details.box.height / 2 -
      textHeight / 2;
  } else if (vAlign == 'bottom') {
    valueY =
      details.y +
      details.box.height -
      padding * 2 -
      textHeight +
      textDimension.h / (details.lineHeight || 1.15);
  }

  return [
    {
      id: `${details.id}_box`,
      value: '',
      x: details.x,
      y: details.y,
      box: {
        height: details.box.height,
        width: details.box.width,
        color: details.box.color,
        strokeColor: details.box.strokeColor,
        strokeWidth: details.box.strokeWidth,
      },
    },
    {
      id: `${details.id}_value`,
      value: details.value,
      x: valueX,
      y: valueY,
      textAlign: details.textAlign,
      maxWidth: details.box.width - padding * 2,
      fontsize: details.fontsize,
      fontStyle: details.fontStyle,
    },
  ];
};

// #region For generating question PDF
export const generateGenericPDFPages = (
  form: FormModel,
  type: GenericFormType
): PDFPage[] => {
  const doc = getDocSetup();

  const {
    PAGE_MARGIN,
    HEADER_FONT_SIZE,
    NUMBERING_WIDTH,
    FONT_SIZE,
    QUESTION_GAP,
    LINE_HEIGHT,
  } = type == 'riskControl' ? RiskControlFormConstants : FormSummaryConstants;

  const startX = PAGE_MARGIN;
  const endX = Math.round(doc.internal.pageSize.getWidth()) - PAGE_MARGIN;
  const startY = PAGE_MARGIN;
  const endY = Math.round(doc.internal.pageSize.getHeight()) - PAGE_MARGIN;
  const firstQuestionY = PAGE_MARGIN + (FONT_SIZE * LINE_HEIGHT + QUESTION_GAP);

  const fullWidthMargin = endX - startX;
  const fullWidthIndent = fullWidthMargin - NUMBERING_WIDTH;

  let currentYPos = startY;

  let allQuestions: QuestionPDF[] = [];
  if (type == 'summary') {
    // Adding compulsory questions
    const compulsory = plottingQuestions(
      doc,
      form.compulsoryQuestions,
      startX,
      fullWidthIndent,
      currentYPos,
      FormSummaryConstants,
      'summary'
    );
    if (compulsory.at(-1) !== undefined) {
      currentYPos = compulsory.at(-1)!.yPos;
    }

    let impact: QuestionPDF[] = [];
    let probability: QuestionPDF[] = [];

    form.getTotalMarks();

    if (form.isSignificant) {
      // Adding impact questions
      impact = plottingQuestions(
        doc,
        form.impactQuestions,
        startX,
        fullWidthIndent,
        currentYPos,
        FormSummaryConstants,
        'summary',
        form.compulsoryQuestions.length
      );
      if (impact.at(-1) !== undefined) {
        currentYPos = impact.at(-1)!.yPos;
      }

      // Adding probability questions
      probability = plottingQuestions(
        doc,
        form.probabilityQuestions,
        startX,
        fullWidthIndent,
        currentYPos,
        FormSummaryConstants,
        'summary',
        form.compulsoryQuestions.length + form.impactQuestions.length
      );
      if (probability.at(-1) !== undefined) {
        currentYPos = probability.at(-1)!.yPos;
      }
    }

    allQuestions = [...compulsory, ...impact, ...probability];
  } else if (type == 'riskControl') {
    const questions = plottingQuestions(
      doc,
      form.riskQuestions,
      startX,
      fullWidthIndent,
      currentYPos,
      RiskControlFormConstants,
      'riskControl'
    );
    if (questions.at(-1) !== undefined) {
      currentYPos = questions.at(-1)!.yPos;
    }
    allQuestions = [...questions];
  }

  // Checking all the questions positions
  let nextPageIndex = allQuestions.findIndex((q) => q.position.bottom > endY);
  while (nextPageIndex >= 0) {
    const topOffset = allQuestions[nextPageIndex].position.top - firstQuestionY;
    for (let x = nextPageIndex; x < allQuestions.length; x++) {
      const current = allQuestions[x];
      current.texts.forEach((ct) => {
        ct.y -= topOffset;
      });

      current.position.top -= topOffset;
      current.position.bottom -= topOffset;
    }

    nextPageIndex = allQuestions.findIndex((q) => q.position.bottom > endY);
  }

  // Split to pages
  const pagesQuestions: QuestionPDF[][] = splitGenericPages(
    allQuestions,
    firstQuestionY
  );

  // Generate PDF pages
  let pdfPages: PDFPage[] = pagesQuestions.map((page, pageInd): PDFPage => {
    const allText = page.map((p) => p.texts).flat();
    const header: PDFText = {
      id: 'formHeader',
      value: pageInd > 0 ? `${form.title} (Cont.)` : form.title,
      x: startX,
      y: startY,
      maxWidth: fullWidthMargin,
      fontsize: HEADER_FONT_SIZE,
      fontStyle: 'bold',
      fullWidth: type == 'summary',
      textAlign: type == 'summary' ? 'center' : 'left',
    };
    const footer: PDFText = {
      id: 'formFooter',
      value: `Page ${pageInd + 1} of ${pagesQuestions.length}`,
      x: endX,
      y: endY + PAGE_MARGIN / 2,
      fontsize: FONT_SIZE,
      textAlign: 'right',
    };

    let texts: PDFText[] = [];
    if (type == 'summary') {
      texts = [header, ...allText];
    } else if (type == 'riskControl') {
      texts = pageInd > 0 ? [...allText, footer] : [header, ...allText, footer];
    }

    return {
      background: '',
      texts: texts,
    };
  });

  return pdfPages;
};

const plottingQuestions = (
  doc: jsPDF,
  questions: Question[] | QuestionWithMarks[],
  startX: number,
  fullWidthIndent: number,
  yPos: number,
  constants: typeof RiskControlFormConstants | typeof FormSummaryConstants,
  type: GenericFormType,
  numberingOffset: number = 0
): QuestionPDF[] => {
  const {
    FONT_SIZE,
    LINE_HEIGHT,
    QUESTION_GAP,
    NUMBERING_WIDTH,
    NOTES_FONT_SIZE,
    NOTES_FONT_COLOR,
  } = constants;

  const allPosition: QuestionPosition[] = [];

  const questionArray = questions.map(
    (comp: Question | QuestionWithMarks, ind: number): PDFText[] => {
      yPos += FONT_SIZE * LINE_HEIGHT + QUESTION_GAP;
      const startYPos = yPos;

      // #region Question section
      const questions: PDFText[] = [
        {
          id: `compulsory_question_numbering_${ind + 1}`,
          value: `${ind + 1 + numberingOffset}.`,
          x: startX,
          y: yPos,
          fontsize: FONT_SIZE,
        },
        {
          id: `compulsory_question_${ind + 1}`,
          value: comp.question,
          maxWidth: fullWidthIndent,
          x: startX + NUMBERING_WIDTH,
          y: yPos,
          fontsize: FONT_SIZE,
        },
      ];

      yPos += getTextHeight(
        doc,
        comp.question,
        fullWidthIndent,
        FONT_SIZE,
        constants
      );
      // #endregion Question section

      if (type == 'summary') {
        // #region Question notes section
        if (comp.questionNotes.length > 0) {
          yPos += NOTES_FONT_SIZE * LINE_HEIGHT;
          const fullQNotes = `** ${comp.questionNotes.join(', ')}`;
          const qNote: PDFText = {
            id: `qNotes_${ind + 1}`,
            value: fullQNotes,
            maxWidth: fullWidthIndent,
            x: startX + NUMBERING_WIDTH,
            y: yPos,
            fontsize: NOTES_FONT_SIZE,
            fontStyle: 'italic',
            fontColor: NOTES_FONT_COLOR,
          };

          yPos += getTextHeight(
            doc,
            fullQNotes,
            fullWidthIndent,
            NOTES_FONT_SIZE,
            constants,
            'italic'
          );

          questions.push(qNote);
        }
        // #endregion Question notes section

        // #region Answer section
        yPos += FONT_SIZE * LINE_HEIGHT;

        const answer: PDFText = {
          id: `answer_${ind + 1}`,
          value: comp.answer,
          maxWidth: fullWidthIndent,
          x: startX + NUMBERING_WIDTH,
          y: yPos,
          fontsize: FONT_SIZE,
          fontStyle: 'bold',
        };

        yPos += getTextHeight(
          doc,
          comp.answer,
          fullWidthIndent,
          FONT_SIZE,
          constants,
          'bold'
        );

        questions.push(answer);
        // #endregion Answer section

        // #region Answer notes section
        if (comp.answerNotes.length > 0) {
          yPos += NOTES_FONT_SIZE * LINE_HEIGHT;
          const fullANotes = `** ${comp.answerNotes.join(', ')}`;
          const aNotes: PDFText = {
            id: `aNotes_${ind + 1}`,
            value: fullANotes,
            maxWidth: fullWidthIndent,
            x: startX + NUMBERING_WIDTH,
            y: yPos,
            fontsize: NOTES_FONT_SIZE,
            fontStyle: 'italic',
            fontColor: NOTES_FONT_COLOR,
          };

          yPos += getTextHeight(
            doc,
            fullANotes,
            fullWidthIndent,
            NOTES_FONT_SIZE,
            constants,
            'italic'
          );

          questions.push(aNotes);
        }
        // #endregion Answer notes section
      } else if (type == 'riskControl') {
        // #region Answer section
        yPos += FONT_SIZE * LINE_HEIGHT;

        const checkBoxSize = 3;
        const answer: PDFText[] = [
          {
            id: 'yesCheckbox',
            value: '',
            x: startX + NUMBERING_WIDTH,
            y: yPos - checkBoxSize * 0.9,
            box: {
              height: checkBoxSize,
              width: checkBoxSize,
              color: comp.answer == 'yes' ? '#D6D6D6' : null,
              strokeColor: '#000000',
            },
          },
          {
            id: 'yesCheck',
            value: '',
            x: startX + NUMBERING_WIDTH,
            y: yPos - checkBoxSize + 0.25,
            fontsize: 3,
            isCheckbox: true,
            hide: comp.answer == 'no',
          },
          {
            id: `answer_${ind + 1}`,
            value: 'Yes',
            maxWidth: fullWidthIndent,
            x: startX + NUMBERING_WIDTH + 5,
            y: yPos,
            fontsize: FONT_SIZE,
          },

          {
            id: 'noCheckbox',
            value: '',
            x: startX + NUMBERING_WIDTH + 5 + 20,
            y: yPos - checkBoxSize * 0.9,
            box: {
              height: checkBoxSize,
              width: checkBoxSize,
              color: comp.answer == 'no' ? '#D6D6D6' : null,
              strokeColor: '#000000',
            },
          },
          {
            id: 'noCheck',
            value: '',
            x: startX + NUMBERING_WIDTH + 5 + 20,
            y: yPos - checkBoxSize + 0.25,
            fontsize: 3,
            isCheckbox: true,
            hide: comp.answer == 'yes',
          },
          {
            id: `answer_${ind + 1}`,
            value: 'No',
            maxWidth: fullWidthIndent,
            x: startX + NUMBERING_WIDTH + 5 + 20 + 5,
            y: yPos,
            fontsize: FONT_SIZE,
          },
        ];

        yPos += getTextHeight(
          doc,
          comp.answer,
          fullWidthIndent,
          FONT_SIZE,
          constants,
          'bold'
        );

        questions.push(...answer);
        // #endregion Answer section
      }

      allPosition.push({ top: startYPos, bottom: yPos });
      return questions;
    }
  );

  return allPosition.map((pos, posInd): QuestionPDF => {
    return {
      questionNumber: posInd + 1 + numberingOffset,
      yPos: yPos,
      position: pos,
      texts: questionArray[posInd],
    };
  });
};

const splitGenericPages = (
  allQuestions: QuestionPDF[],
  firstQuestionY: number
): QuestionPDF[][] => {
  const pagesQuestions: QuestionPDF[][] = [];
  const firstQuestionOfPage = allQuestions.filter(
    (q) => q.position.top.toFixed(4) === firstQuestionY.toFixed(4)
  );
  const firstQuestionOfPageIndex = firstQuestionOfPage.map((first) =>
    allQuestions.findIndex((q) => q.questionNumber == first.questionNumber)
  );
  for (let x = 0; x < firstQuestionOfPageIndex.length; x++) {
    const currentIndex = firstQuestionOfPageIndex[x];
    const nextIndex = firstQuestionOfPageIndex[x + 1];

    pagesQuestions.push(allQuestions.slice(currentIndex, nextIndex));
  }

  return pagesQuestions;
};

const getTextHeight = (
  doc: jsPDF,
  text: string,
  maxWidth: number,
  fontsize: number,
  constants: typeof RiskControlFormConstants | typeof FormSummaryConstants,
  fontStyle: FontStyle = 'normal'
): number => {
  const { LINE_HEIGHT } = constants;

  const textLines = doc.splitTextToSize(text, maxWidth, {
    fontSize: fontsize,
    fontStyle: fontStyle,
  });

  return (textLines.length - 1) * (fontsize * LINE_HEIGHT);
};
// #endregion For generating question PDF
